import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import IconButton from 'LuxKit/Button/IconButton'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import LineRedoIcon from 'LuxKit/Icons/line/LineRedoIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import LineThumbsDownIcon from 'LuxKit/Icons/line/LineThumbsDownIcon'
import SolidThumbsUpIcon from 'LuxKit/Icons/solid/SolidThumbsUpIcon'
import SolidThumbsDownIcon from 'LuxKit/Icons/solid/SolidThumbsDownIcon'
import Tooltip from 'LuxKit/Tooltip'

interface Props {
  onCopy?: () => void
  copiedRecently: boolean
  disableCopyButton: boolean
  onRegenerate?: () => void
  disableRegenerateButton: boolean
  onThumbsUp?: () => void
  onThumbsDown?: () => void
  isThumbsIconDisabled: boolean
  isThumbsUp: boolean
  isThumbsDown: boolean
}

function AssistantResponseToolbar({
  onCopy = noop,
  copiedRecently,
  disableCopyButton,
  onRegenerate = noop,
  disableRegenerateButton,
  onThumbsUp = noop,
  onThumbsDown = noop,
  isThumbsIconDisabled,
  isThumbsUp,
  isThumbsDown,
}: Props) {
  const intl = useIntl()

  return (
    <Group direction="horizontal" gap={12} data-assistant-toolbar>
      <Tooltip
        description={intl.formatMessage({
          id: 'OvLjjFBEG8Yef7zuNbsz',
          defaultMessage: 'Copy',
          description: 'Tooltip for the copy response button in the AI assistant toolbar',
        })}
        placement="top"
      >
        <IconButton
          kind="tertiary"
          shape="circle"
          onClick={onCopy}
          disabled={disableCopyButton}
          aria-label={intl.formatMessage({
            id: 'QDmgVTLCKz8qaiiuOywm',
            defaultMessage: 'Copy response',
            description: 'Aria-label for the copy response button in the AI assistant toolbar',
          })}
        >
          {copiedRecently && <LineCheckIcon size="S" />}
          {!copiedRecently && <LineCopyIcon size="S" />}
        </IconButton>
      </Tooltip>
      <Tooltip
        description={intl.formatMessage({
          id: 'A_S22YKvCChUk0cURAhv',
          defaultMessage: 'Try again',
          description: 'Tooltip for the regenerate response button in the AI assistant toolbar',
        })}
        placement="top"
      >
        <IconButton
          kind="tertiary"
          shape="circle"
          onClick={onRegenerate}
          disabled={disableRegenerateButton}
          aria-label={intl.formatMessage({
            id: 'GmemqJWUKo_R7vQE463B',
            defaultMessage: 'Regenerate response',
            description: 'Aria-label for the regenerate response button in the AI assistant toolbar',
          })}
        >
          <LineRedoIcon size="S" />
        </IconButton>
      </Tooltip>
      <Tooltip
        description={intl.formatMessage({
          id: '82ib-z577hYebJuf-a2-',
          defaultMessage: 'Good response',
          description: 'Tooltip for the thumbs up feedback button in the AI assistant toolbar',
        })}
        placement="top"
      >
        <IconButton
          kind="tertiary"
          shape="circle"
          onClick={onThumbsUp}
          disabled={isThumbsIconDisabled}
          aria-label={intl.formatMessage({
            id: 'j8EFdj7-FMuXU8XCe8Ep',
            defaultMessage: 'Thumbs up',
            description: 'Aria-label for the thumbs up feedback button in the AI assistant toolbar',
          })}
        >
          {isThumbsUp && <SolidThumbsUpIcon size="S" />}
          {!isThumbsUp && <LineThumbsUpIcon size="S" />}
        </IconButton>
      </Tooltip>
      <Tooltip
        description={intl.formatMessage({
          id: 'SUDkgdNTAHSQ8jXjRndU',
          defaultMessage: 'Bad response',
          description: 'Tooltip for the thumbs down feedback button in the AI assistant toolbar',
        })}
        placement="top"
      >
        <IconButton
          kind="tertiary"
          shape="circle"
          onClick={onThumbsDown}
          disabled={isThumbsIconDisabled}
          aria-label={intl.formatMessage({
            id: 'L5i4_wz-mB753BFcGEAq',
            defaultMessage: 'Thumbs down',
            description: 'Aria-label for the thumbs down feedback button in the AI assistant toolbar',
          })}
        >
          {isThumbsDown && <SolidThumbsDownIcon size="S" />}
          {!isThumbsDown && <LineThumbsDownIcon size="S" />}
        </IconButton>
      </Tooltip>
    </Group>
  )
}

export default AssistantResponseToolbar
